<template>
  <NoteSection
    label="Product Information"
    ref="productInfo"
    required
    :highlight-required="isSectionIncomplete"
    :form-validated="formValidated"
  >
    <p
      v-if="canEditAtgProduct"
      class="col-sm-12"
    >
      Enter the name of the amniotic tissue graft product used.
      <v-combobox
        :attach="true"
        class="mt-2 required-field"
        v-model="product_used"
        :items="atgProductList"
        placeholder="Select or enter a product"
        allow-overflow
        :maxlength="100"
        outlined
        counter
        single-line
        :clear-icon="icons.mdiClose"
        :no-filter="true"
        :rules="[v => (v && v.trim() !== '') || 'Required field']"
        @input.native="product_used=$event.srcElement.value"
        @focus="scrollIntoProductInfo"
      >
        <template v-if="!product_used" #label>
          <strong class="error--text">*</strong>
        </template>
      </v-combobox>
    </p>
    <p
      v-if="!canEditAtgProduct"
      class="col-sm-12"
    >
      Amniotic tissue graft product used (Carbonlife is the only authorized product):
      <text-field
        class="mt-2 required-field"
        v-model="product_used"
        label="Amniotic tissue graft product used (per management, Carbonlife is the only authorized product):"
        outlined
        single-line
        readonly
        required
        @focus="scrollIntoProductInfo"
      >
        <template v-if="!product_used" #label>
          <strong class="error--text">*</strong>
        </template>
      </text-field>
    </p>

    <v-divider class="col-12 mb-4"/>
    <v-container>
      <div class="d-flex align-center justify-space-between mb-2">
        <span class="body-0">
          Indicate the quantity and dimensions of each graft section used for this treatment:
        </span>
        <v-btn
        color="primary"
        type="button"
        @click="addAmnioGraft"
        >
          <v-icon>{{ icons.mdiPlus }}</v-icon>
        </v-btn>
      </div>
      <v-card>
          <v-data-table
            ref="graftTable"
            :headers="headers"
            :items="nonDeletedAmnioGrafts"
            fixed-header
            height="280"
            item-value="id"
            class="elevation-1"
            hide-default-footer
            disable-sort
            :items-per-page="amnio_grafts.length"
          >
            <!-- Center each header manually -->
            <template v-slot:header.index>
              <div class="text-center">#</div>
            </template>

            <template v-slot:header.graft_length>
              <div class="text-center">Length (cm)</div>
            </template>

            <template v-slot:header.graft_width>
              <div class="text-center">Width (cm)</div>
            </template>

            <template v-slot:header.graft_qty>
              <div class="text-center">Quantity used</div>
            </template>

            <template v-slot:header.actions>
              <div class="text-center"></div>
            </template>


            <!-- Row Count -->
            <template v-slot:item.index="{ index }">
              {{ index + 1 }}
            </template>

            <!-- Length -->
            <template v-slot:item.graft_length="{ item }">
              <text-field
                class="mt-1"
                v-model="item.graft_length"
                single-line
                numeric
                required
                :mask="decimalMask"
                rules="numberRange(0.1,100)"
                @blur="item.graft_length =
                (item.graft_length >= 0.1 && item.graft_length <= 100)
                  ? Number(item.graft_length || 0).toFixed(2)
                  : null
                  calculateTotalArea(item)"
              />
            </template>

            <!-- Width -->
            <template v-slot:item.graft_width="{ item }">
              <text-field
                class="mt-1"
                v-model="item.graft_width"
                single-line
                numeric
                required
                :mask="decimalMask"
                rules="numberRange(0.1,100)"
                @blur="item.graft_width =
                (item.graft_width >= 0.1 && item.graft_width <= 100)
                  ? Number(item.graft_width || 0).toFixed(2)
                  : null
                  calculateTotalArea(item)"
              />
            </template>

            <!-- Qty Used -->
            <template v-slot:item.graft_qty="{ item }">
              <text-field
                class="mt-1"
                v-model="item.graft_qty"
                single-line
                numeric
                required
                :mask="decimalMask"
                rules="numberRange(0.1,999)"
                @blur="item.graft_qty =
                (item.graft_qty >= 0.1 && item.graft_qty <= 999)
                  ? item.graft_qty
                  : null
                  calculateTotalArea(item)"
              />
            </template>

            <!-- Delete -->
            <template v-slot:item.actions="{ item }">
              <v-btn
                v-if="nonDeletedAmnioGrafts.length > 1"
                icon
                @click="deleteRow(item)"
              >
                <v-icon color="error">{{ icons.mdiDelete }}</v-icon>
              </v-btn>
            </template>
          </v-data-table>
      </v-card>
    </v-container>


    <p class="col-sm-12 mt-10">
      Indicate if any of the tissue graft product was wasted (any folded area is still counted toward total area):
      <select-box
        class="mt-2"
        v-model="product_wasted"
        :items="$store.getters['baseData/atgProductWasted']"
        :menu-props="{ top: !isProductWasted, offsetY: true}"
        single-line
        required
      />
    </p>
    <p
      v-if="isProductWasted"
      class="col-sm-12 mb-1"
    >
      Indicate the quantity of the tissue graft that was wasted:
    </p>
    <div
      v-if="isProductWasted"
      class="col-sm-12 d-flex align-items-start mb-3"
    >
      <text-field
        ref="quantityWastedField"
        :class="['col-sm-3', 'mr-2', { 'blur-effect': !isProductWasted }]"
        v-model="quantity_wasted"
        numeric
        :mask="decimalMask"
        rules="numberRange(0,999)"
        required
        not-clearable
        label="Qty"
        outlined
        suffix="cm²"
        :disabled="!isProductWasted"
        @blur="quantity_wasted =
          (quantity_wasted && quantity_wasted >= 0 && quantity_wasted <= 999)
            ? Number(quantity_wasted || 0).toFixed(3)
            : null"
      />
      <btn
        label="Calculate"
        :disabled="!isProductWasted"
        :icon="icons.mdiHeadSnowflake"
        @click="validateAndCalculateWastedAmount"
      />
    </div>
    <p
      v-if="isProductWasted"
      class="col-sm-12"
    >
      Explain the reason why any amniotic tissue graft product went to waste:
      <text-area
        ref="quantityWastedReasonField"
        v-model="reason_wasted"
        label="Reason"
        :class="['mt-2', { 'blur-effect': !isProductWasted }]"
        required
        clear-warning
        rows="3"
        :disabled="!isProductWasted"
      />
    </p>
  </NoteSection>
</template>

<script>
import { mdiClose, mdiHeadSnowflake, mdiDelete, mdiPlus } from '@mdi/js'
import { v4 as uuidv4 } from 'uuid'

export default {
  name: 'AmnioMedicalNecessity',
  props: {
    treatmentId: {
      type: String,
      default: null,
    },
    productUsed: {
      type: String,
      default: null,
    },
    productWasted: {
      type: String,
      default: null,
    },
    quantityWasted: {
      type: [Number, String],
      default: null,
    },
    reasonWasted: {
      type: String,
      default: null,
    },
    dimLength: {
      type: [Number, String],
      default: null,
    },
    dimWidth: {
      type: [Number, String],
      default: null,
    },
    amnioGrafts: {
      type: Array,
      default: () => []
    },
    encounterCreatedDate: {
      type: [String, Date],
      default: null,
    },
    // Form validation state passed down
    formValidated: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      icons: { mdiClose, mdiHeadSnowflake, mdiDelete, mdiPlus },
      product_used: this.productUsed,
      product_wasted: this.productWasted,
      quantity_wasted: this.quantityWasted,
      reason_wasted: this.reasonWasted,
      amnio_grafts: this.amnioGrafts,
      decimalMask: [/[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/, /[0-9|.]/],
      headers: [
        { text: "#", value: "index", width: "40px", align: 'center' },
        { text: "Length (cm)", value: "graft_length", align: 'center' },
        { text: "Width (cm)", value: "graft_width", align: 'center' },
        { text: "Quantity used", value: "graft_qty", align: 'center' },
        { text: "", value: "actions", width: "40px", align: 'center' },
      ],
    }
  },
  computed: {
    canEditAtgProduct() {
      if (!this.encounterCreatedDate) {
        return false;
      }

      // EF20260120 Per TXP-692: encounters before 2026-01-22 can modify Product Used.
      // From that date forward, only Carbonlife should be selectable.

      const cutoffDate = new Date('2026-01-22');
      const encounterCreationDate = this.encounterCreatedDate instanceof Date
        ? this.encounterCreatedDate
        : new Date(this.encounterCreatedDate);

      return encounterCreationDate < cutoffDate;
    },
    atgProductList() {
      return this.$store.getters['baseData/atgProductList']
    },
    nonDeletedAmnioGrafts() {
      return this.amnio_grafts.filter(item => !item.deleted)
    },
    isProductWasted() {
      return this.product_wasted === 'Some product was wasted.'
    },

    // Evaluates incomplete state for section highlight
    isSectionIncomplete() {
      const hasProductUsed = !!this.product_used && this.product_used.trim() !== ''
      const hasProductWastedSelection = !!this.product_wasted

      const hasInvalidGrafts = this.nonDeletedAmnioGrafts.length === 0 || this.nonDeletedAmnioGrafts.some(g =>
        g.graft_length == null || String(g.graft_length).trim() === '' ||
        g.graft_width == null || String(g.graft_width).trim() === '' ||
        g.graft_qty == null || String(g.graft_qty).trim() === ''
      )

      if (!hasProductUsed || !hasProductWastedSelection || hasInvalidGrafts) {
        return true
      }

      if (this.isProductWasted) {
        const hasQtyWasted = this.quantity_wasted !== null && this.quantity_wasted !== undefined && String(this.quantity_wasted).trim() !== ''
        const hasReasonWasted = !!this.reason_wasted && this.reason_wasted.trim() !== ''

        if (!hasQtyWasted || !hasReasonWasted) return true
      }

      return false
    },
  },
  watch: {
    product_used(newVal) {
      this.$emit('update:product-used', newVal)
    },
    product_wasted(newVal) {
      this.$emit('update:product-wasted', newVal)

      this.$nextTick(() => {
        if (this.isProductWasted) {
          const qtyField = this.$refs.quantityWastedField?.$el?.querySelector('input')
          const reasonField = this.$refs.quantityWastedReasonField?.$el?.querySelector('textarea')
          if (qtyField) qtyField.focus()
          this.$nextTick(() => {
            if (reasonField) reasonField.focus()
            this.$nextTick(() => {
              if (reasonField) reasonField.blur()
            })
          })
        } else {
          this.quantity_wasted = null
          this.reason_wasted = null
        }
      })
    },
    quantity_wasted(newVal) {
      this.$emit('update:quantity-wasted', newVal)
    },
    reason_wasted(newVal) {
      this.$emit('update:reason-wasted', newVal)
    },
    amnio_grafts(newVal) {
      this.$emit('update:amnio-grafts', newVal)
    },
  },
  async created() {
    if (this.nonDeletedAmnioGrafts.length < 1) {
      this.addAmnioGraft()
    }

    // EF20260119: Per TXP-692 and Fernando, Carbonlife will be the default selection moving forward
    // Previous selection will remain intact.
    if (!this.canEditAtgProduct && !this.product_used) {
      this.product_used = 'Carbonlife'
    }
  },
  methods: {
    scrollIntoProductInfo() {
      this.$nextTick(() => {
        const el = this.$refs.productInfo?.$el || this.$refs.productInfo
        if (el?.scrollIntoView) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
        }
      })
    },
    addAmnioGraft() {
      // Only allow adding of new records if there are no invalid entries.
      const hasInvalid = this.nonDeletedAmnioGrafts.some(g =>
        g.total_area <= 0 ||
        g.graft_length < 0.1 || g.graft_length > 100 ||
        g.graft_width  < 0.1 || g.graft_width  > 100 ||
        g.graft_qty    < 0.1 || g.graft_qty    > 999
      )

      if (hasInvalid) {
        const msg = 'Please complete the existing incomplete graft entry before adding a new one. The entry must be valid.'
        this.$store.dispatch('notify', {
          value: msg,
          color: 'warning',
          timeout: 5000
        })
        return
      }

      // Add new record
      this.amnio_grafts.push({
        id: uuidv4(),
        treatment_id: this.treatmentId,
        graft_length: null,
        graft_width: null,
        graft_qty: null,
        total_area: null,
        can_be_deleted: true,
      })

      // Scroll to bottom after DOM updates
      this.$nextTick(() => {
        const wrapper = this.$refs.graftTable?.$el.querySelector('.v-data-table__wrapper')
        if (wrapper) {
          wrapper.scrollTop = wrapper.scrollHeight
        }
      })
    },
    deleteRow(item) {
      if (item.can_be_deleted) {
        // Remove item if can_be_deleted
        this.amnio_grafts = this.amnio_grafts.filter(i => i.id !== item.id)
      } else {
        // Otherwise, mark item as deleted
        const index = this.amnio_grafts.findIndex(i => i.id === item.id)
        if (index !== -1) {
          this.$set(this.amnio_grafts[index], 'deleted', true)
        }
      }
    },
    calculateTotalArea(item) {
      const length = parseFloat(item.graft_length)
      const width = parseFloat(item.graft_width)
      const qty = parseFloat(item.graft_qty)

      const hasAllValues =
        item.graft_length?.toString().trim() !== '' &&
        item.graft_width?.toString().trim() !== '' &&
        item.graft_qty?.toString().trim() !== ''

      if (hasAllValues && !isNaN(length) && !isNaN(width) && !isNaN(qty)) {
        item.total_area = (length * width * qty).toFixed(3)
      } else {
        item.total_area = null
      }
    },
    validateAndCalculateWastedAmount() {
      const errors = []

      // 1. Validate wound length & width
      const hasWoundLength = this.dimLength != null && this.dimLength?.toString().trim() !== ''
      const hasWoundWidth = this.dimWidth != null && this.dimWidth?.toString().trim() !== ''
      if (!hasWoundLength || !hasWoundWidth) {
        errors.push("wound dimensions have not yet been entered")
      }

      // 2. Validate at least one complete graft row (length, width, qty)
      const validGraftRows = this.nonDeletedAmnioGrafts.filter(g =>
        g.graft_length != null &&
        g.graft_width != null &&
        g.graft_qty != null &&
        g.graft_length.toString().trim() !== '' &&
        g.graft_width.toString().trim() !== '' &&
        g.graft_qty.toString().trim() !== ''
      )

      if (validGraftRows.length === 0) {
        errors.push("quantity of tissue graft product used has not been fully entered")
      }

      // 3. If there are validation errors, show modal with those messages
      if (errors.length > 0) {
        const formattedError =
          'The amount of product wasted could not be calculated:\n' +
          errors.join(' and ') + '.' +
          '\nPlease fill in required information and try again.'

        this.$store.dispatch('notify', {
          value: formattedError,
          color: 'error',
          timeout: 10000
        })

        return
      }

      // 4. Calculate wound area and graft material used
      const woundArea = parseFloat(this.dimLength) * parseFloat(this.dimWidth)

      const graftUsed = validGraftRows.reduce((sum, row) => {
        const length = parseFloat(row.graft_length)
        const width = parseFloat(row.graft_width)
        const qty = parseFloat(row.graft_qty)
        return sum + (length * width * qty)
      }, 0)

      const amountWasted = graftUsed - woundArea

      // 5. If amountWasted < 0, show specific error
      if (amountWasted < 0) {
        const message = `The wound is indicated as being ${woundArea.toFixed(2)} cm² in area,
         but you have indicated that you have only used ${graftUsed.toFixed(2)} cm² of amniotic tissue
         graft material. Please review your entries or enter the amount of product wasted manually.`

        this.$store.dispatch('notify', {
          value: message,
          color: 'error',
          timeout: 10000
        })
        return
      }

      // 6. If amountWasted > 999, show specific error
      if (amountWasted > 999) {
        const message = `Value exceeds the maximum allowed: 999.`

        this.$store.dispatch('notify', {
          value: message,
          color: 'error',
          timeout: 3000
        })
        return
      }

      // 7. If valid, assign to quantity_wasted
      this.quantity_wasted = null
      this.$nextTick(() => {
        this.quantity_wasted = amountWasted.toFixed(3)
      })
    }
  },
}
</script>
<style lang="scss">
.blur-effect {
  filter: blur(1px);
  pointer-events: none;
}
</style>
